"use client";

import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { computeKeyboardZoomStep } from "@/lib/price-chart-utils";

export interface UseDragZoomOptions {
  /** Kleinster/groesster Wert der GESAMTEN (ungezoomten) Datenreihe entlang der X-Achse -
   * z.B. Unix-Millisekunden bei Tages-/Wochendaten oder ein rohes Kalenderjahr bei
   * Jahresreihen. Die Domaene ist bewusst generisch/einheitenlos - Mausrad-/Tastatur-Zoom
   * skaliert relativ zur Gesamtspanne, der Hook selbst muss die Einheit nicht kennen.
   * `undefined`, solange noch keine Daten geladen sind (deaktiviert Wheel/Tastatur bis
   * dahin, gleiches Verhalten wie ein leerer Chart). */
  fullMin: number | undefined;
  fullMax: number | undefined;
  /** Kleinste erlaubte Zoom-Spanne, gleiche Einheit wie fullMin/fullMax (z.B. 3 Tage in ms,
   * oder 1 fuer ein Kalenderjahr) - verhindert Zoom in einen unbrauchbaren Splitter. */
  minSpan: number;
  /** Deaktiviert Maus-, Mausrad- UND Tastatur-Zoom komplett (siehe historical-line-chart.tsx
   * -> disableZoom-Prop, z.B. fuer eine reine Prognosekurve ohne Ausschnitts-Mehrwert). */
  disabled?: boolean;
}

export interface UseDragZoomResult {
  zoomRange: [number, number] | null;
  resetZoom: () => void;
  refAreaLeft: number | null;
  refAreaRight: number | null;
  /** Auf denselben Chart-Wrapper-<div> setzen, den auch die ResponsiveContainer-Hoehe
   * bestimmt - traegt den Fokus fuer die Tastatur-Bedienung. */
  chartWrapperRef: RefObject<HTMLDivElement | null>;
  onMouseDown: (e: { activeLabel?: string | number }) => void;
  onMouseMove: (e: { activeLabel?: string | number }) => void;
  onMouseUp: () => void;
  /** Auf denselben Wrapper wie chartWrapperRef setzen (zusammen mit keyboardA11yProps). */
  onKeyDown: (e: KeyboardEvent) => void;
  /** tabIndex/role/aria-label fuer den fokussierbaren Chart-Wrapper - leer (kein Objekt-
   * Spread noetig), wenn `disabled`. */
  keyboardA11yProps: { tabIndex: number; role: "group"; "aria-label": string } | Record<string, never>;
}

/**
 * Gemeinsamer Klick-Zieh-Zoom-Hook fuer Zeitreihen-Charts (backlog.md -> Accessibility-
 * Audit "Chart-Klick-Zieh-Zoom hat keine Tastatur-Alternative...") - konsolidiert ein
 * zuvor 6x fast identisch copy-paste-dupliziertes Muster (refAreaLeft/refAreaRight/
 * zoomRange + rAF-gedrosselte Maus-Handler) aus price-history-chart.tsx/
 * historical-line-chart.tsx/multi-kpi-line-chart.tsx/peer-kpi-comparison.tsx/
 * peer-compare.tsx/competitor-price-race-chart.tsx, UND ergaenzt neue Tastatur-Bedienung
 * fuer alle sechs. **technical-chart.tsx bleibt bewusst aussen vor**: dort lebt die
 * Zoom-Logik in einem separaten, aus Render-Performance-Gruenden memoisierten
 * Kind-Element (`PriceChartCore`) neben einem eigenen Zeichenwerkzeug-Overlay - ein Umbau
 * auf diesen Hook haette fuer die ohnehin komplexeste Chart-Komponente ein groesseres
 * Regressions-Risiko bedeutet, als der Konsistenz-Gewinn hier rechtfertigt (siehe
 * STATUS.md). Tastatur fehlt dort weiterhin - eigener, gezielter Folge-Schritt.
 *
 * **Mausrad-Zoom bewusst ENTFERNT (2026-08-18, Erik-Feedback):** scrollte man mit dem
 * Mausrad ueber einen Chart, wurde bisher gezoomt statt die Seite normal zu scrollen -
 * Erik empfand das als stoerend ("die Funktion mag ich nicht") und bat ausdruecklich um
 * Ruecknahme. Nicht wieder einfuehren, auch nicht fuer technical-chart.tsx oder neue
 * Charts - reine Klick-Zieh- und Tastatur-Bedienung bleiben die einzigen Zoom-Wege.
 *
 * Tastatur-Bedienung (neu, `computeKeyboardZoomStep` in lib/price-chart-utils.ts): Shift+
 * Pfeil-rechts erweitert die Auswahl nach rechts (legt bei leerer Auswahl den linken Rand
 * in der Mitte des sichtbaren Bereichs an), Shift+Pfeil-links symmetrisch nach links,
 * Enter uebernimmt die aktuelle Auswahl als Zoom (identische Commit-Logik wie
 * `onMouseUp`), Escape verwirft eine begonnene Tastatur-Auswahl ohne zu zoomen. Der
 * Aufrufer muss den zurueckgegebenen `chartWrapperRef`-<div> fokussierbar machen
 * (`{...keyboardA11yProps}` + `onKeyDown`), sonst erreicht kein Tastatur-Event den Hook.
 */
export function useDragZoom({ fullMin, fullMax, minSpan, disabled = false }: UseDragZoomOptions): UseDragZoomResult {
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);
  const [zoomRange, setZoomRange] = useState<[number, number] | null>(null);

  const dragRafRef = useRef<number | null>(null);
  const pendingRightRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (dragRafRef.current !== null) cancelAnimationFrame(dragRafRef.current);
    },
    [],
  );

  function handleMouseDown(e: { activeLabel?: string | number }) {
    if (disabled || e?.activeLabel === undefined) return;
    setRefAreaLeft(Number(e.activeLabel));
  }
  function handleMouseMove(e: { activeLabel?: string | number }) {
    if (disabled || refAreaLeft === null || e?.activeLabel === undefined) return;
    pendingRightRef.current = Number(e.activeLabel);
    if (dragRafRef.current !== null) return;
    dragRafRef.current = requestAnimationFrame(() => {
      dragRafRef.current = null;
      if (pendingRightRef.current !== null) setRefAreaRight(pendingRightRef.current);
    });
  }
  function commitSelection() {
    if (refAreaLeft !== null && refAreaRight !== null && Math.abs(refAreaRight - refAreaLeft) >= minSpan) {
      setZoomRange([Math.min(refAreaLeft, refAreaRight), Math.max(refAreaLeft, refAreaRight)]);
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }
  function handleMouseUp() {
    if (disabled) return;
    commitSelection();
  }

  const chartWrapperRef = useRef<HTMLDivElement | null>(null);

  function handleKeyDown(e: KeyboardEvent) {
    if (disabled || fullMin === undefined || fullMax === undefined) return;
    if (e.key === "Escape" && (refAreaLeft !== null || refAreaRight !== null)) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    if (e.key === "Enter" && refAreaLeft !== null && refAreaRight !== null) {
      e.preventDefault();
      commitSelection();
      return;
    }
    if (!e.shiftKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    const { left, right } = computeKeyboardZoomStep(
      { left: refAreaLeft, right: refAreaRight },
      zoomRange,
      fullMin,
      fullMax,
      e.key === "ArrowRight" ? "right" : "left",
    );
    setRefAreaLeft(left);
    setRefAreaRight(right);
  }

  return {
    zoomRange,
    resetZoom: () => setZoomRange(null),
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onKeyDown: handleKeyDown,
    keyboardA11yProps: disabled
      ? {}
      : {
          tabIndex: 0,
          role: "group",
          "aria-label": "Chart-Zeitraum. Umschalttaste mit Pfeiltasten wählt einen Zoom-Bereich, Eingabetaste übernimmt ihn, Escape verwirft ihn.",
        },
  };
}
